import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader, Card, StatusPill } from '@/components/Primitives';
import { Modal, Field } from './Extensions';
import { Plus, PencilSimple, Trash, ArrowDown, ArrowUp } from '@phosphor-icons/react';
import { useToast } from '@/hooks/use-toast';

// Reusable component for inbound/outbound route lists
const RouteList = ({ kind, title, eyebrow, description, columns, blank, renderRow, modalContent, extraActions, refreshEventName }) => {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const { toast } = useToast();
    const endpoint = kind === 'inbound' ? '/inbound-routes' : '/outbound-routes';

    const load = async () => {
        const r = await api.get(endpoint);
        setItems(r.data);
    };
    useEffect(() => { load(); }, []);
    useEffect(() => {
        if (!refreshEventName) return undefined;
        const handler = () => load();
        window.addEventListener(refreshEventName, handler);
        return () => window.removeEventListener(refreshEventName, handler);
    }, [refreshEventName]);

    const save = async (e) => {
        e.preventDefault();
        try {
            if (editing.id) await api.put(`${endpoint}/${editing.id}`, editing);
            else await api.post(endpoint, editing);
            setEditing(null); load();
            toast({ title: 'Uloženo' });
        } catch (err) {
            toast({ title: 'Chyba', description: err.response?.data?.detail || 'error', variant: 'destructive' });
        }
    };
    const del = async (id) => {
        if (!window.confirm('Smazat pravidlo?')) return;
        await api.delete(`${endpoint}/${id}`); load();
    };

    return (
        <>
            <PageHeader
                eyebrow={eyebrow}
                title={title}
                description={description}
                actions={
                    <div className="flex items-center gap-2">
                        {extraActions}
                        <button onClick={() => setEditing({ ...blank })}
                                data-testid={`${kind}-create-btn`}
                                className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.08em]"
                                style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>
                            <Plus size={14} weight="bold" />Nové pravidlo
                        </button>
                    </div>
                }
            />
            <Card>
                {items.length === 0 ? (
                    <div className="py-8 text-center text-sm font-mono" style={{ color: 'var(--text-secondary)' }}>
                        Žádná pravidla. Pravidla se vyhodnocují podle priority (vzestupně).
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-[0.1em]" style={{ color: 'var(--text-secondary)' }}>
                                {columns.map((c) => <th key={c.k} className={c.align || 'text-left'}>{c.label}</th>)}
                                <th className="text-right">Akce</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((r) => (
                                <tr key={r.id} className="border-t" style={{ borderColor: 'var(--border-default)' }}>
                                    {renderRow(r)}
                                    <td className="text-right">
                                        <button onClick={() => setEditing({ ...r })} className="p-1 hover:bg-[var(--bg-surface-hover)]" data-testid={`${kind}-edit-${r.id}`}><PencilSimple size={14} /></button>
                                        <button onClick={() => del(r.id)} className="p-1 hover:bg-[var(--bg-surface-hover)] ml-1" data-testid={`${kind}-delete-${r.id}`}><Trash size={14} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>
            {editing && (
                <Modal wide title={editing.id ? 'Editace pravidla' : 'Nové pravidlo'} onClose={() => setEditing(null)}>
                    <form onSubmit={save}>
                        {modalContent(editing, setEditing)}
                        <div className="flex justify-end gap-2 mt-4">
                            <button type="button" onClick={() => setEditing(null)} className="px-3 py-2 text-xs font-bold uppercase border" style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>Zrušit</button>
                            <button type="submit" data-testid={`${kind}-save-btn`} className="px-3 py-2 text-xs font-bold uppercase" style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>Uložit</button>
                        </div>
                    </form>
                </Modal>
            )}
        </>
    );
};

export const InboundRoutes = () => {
    const [exts, setExts] = useState([]);
    const [queues, setQueues] = useState([]);
    const [confs, setConfs] = useState([]);
    const [flows, setFlows] = useState([]);
    const [groups, setGroups] = useState([]);
    const [conds, setConds] = useState([]);
    const [provisioning, setProvisioning] = useState(false);
    const { toast } = useToast();

    const loadOptions = () => Promise.all([
        api.get('/extensions'), api.get('/queues'), api.get('/conferences'),
        api.get('/call-flows'), api.get('/ring-groups'), api.get('/time-conditions'),
    ]).then(([e, q, c, f, g, tc]) => {
        setExts(e.data); setQueues(q.data); setConfs(c.data);
        setFlows(f.data); setGroups(g.data); setConds(tc.data);
    });

    useEffect(() => { loadOptions(); }, []);

    const autoProvision = async () => {
        setProvisioning(true);
        try {
            const r = await api.post('/inbound-routes/auto-provision');
            await loadOptions();
            toast({
                title: 'Hotovo',
                description: `Vytvořeno: ${r.data.inbound_routes_created} pravidel, ${r.data.dids_linked_to_default_flow} DID napojeno na výchozí Call Flow.`,
            });
            // Force RouteList to refetch by reloading the page section
            window.dispatchEvent(new Event('inbound-routes-refresh'));
        } catch (err) {
            toast({ title: 'Chyba auto-provision', description: err.response?.data?.detail || 'error', variant: 'destructive' });
        } finally {
            setProvisioning(false);
        }
    };

    const destOptions = (type) => {
        switch (type) {
            case 'extension': return exts.map((e) => ({ v: e.id, l: `${e.extension} · ${e.display_name}` }));
            case 'ring_group': return groups.map((g) => ({ v: g.id, l: g.name }));
            case 'queue': return queues.map((q) => ({ v: q.id, l: `${q.extension_number} · ${q.name}` }));
            case 'conference': return confs.map((c) => ({ v: c.id, l: `${c.extension_number} · ${c.name}` }));
            case 'call_flow': return flows.map((f) => ({ v: f.id, l: f.name }));
            default: return [];
        }
    };

    const DestSelector = ({ value, onTypeChange, onIdChange, types, label, extValue, onExtChange }) => (
        <div className="grid grid-cols-2 gap-2">
            <div>
                <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-secondary)' }}>{label} - typ</label>
                <select value={value.type || ''} onChange={(e) => onTypeChange(e.target.value)}
                        className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm"
                        style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                    <option value="">—</option>
                    {types.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
            </div>
            <div>
                <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-secondary)' }}>{label} - cíl</label>
                {value.type === 'external' ? (
                    <input value={extValue || ''} onChange={(e) => onExtChange(e.target.value)}
                           placeholder="+420..."
                           className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm font-mono"
                           style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                ) : value.type ? (
                    <select value={value.id || ''} onChange={(e) => onIdChange(e.target.value)}
                            className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm"
                            style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                        <option value="">—</option>
                        {destOptions(value.type).map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                    </select>
                ) : (
                    <input disabled className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm opacity-50"
                           style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                )}
            </div>
        </div>
    );

    return (
        <RouteList
            kind="inbound"
            eyebrow="Routing"
            title="Inbound Routes"
            description="Yeastar-style pravidla pro směrování příchozích hovorů. Vyhodnocují se podle priority a typu DID/caller pattern."
            refreshEventName="inbound-routes-refresh"
            extraActions={
                <button onClick={autoProvision} disabled={provisioning}
                        data-testid="inbound-auto-provision-btn"
                        className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.08em] border"
                        style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)', borderRadius: 2, opacity: provisioning ? 0.5 : 1 }}>
                    {provisioning ? 'Provisioning…' : 'Auto-vytvořit z DID'}
                </button>
            }
            blank={{
                name: '', did_pattern: '', caller_id_pattern: '', time_condition_id: null,
                destination_type: 'extension', destination_id: null, external_number: '',
                fallback_destination_type: null, fallback_destination_id: null,
                after_hours_destination_type: null, after_hours_destination_id: null,
                enable_recording: false, is_active: true, priority: 100,
            }}
            columns={[
                { k: 'priority', label: 'Priorita' }, { k: 'name', label: 'Název' },
                { k: 'did', label: 'DID pattern' }, { k: 'dest', label: 'Destinace' },
                { k: 'rec', label: 'Recording', align: 'text-center' },
                { k: 'st', label: 'Stav', align: 'text-center' },
            ]}
            renderRow={(r) => (
                <>
                    <td className="py-2 font-mono">{r.priority}</td>
                    <td>{r.name}</td>
                    <td className="font-mono text-xs">{r.did_pattern}</td>
                    <td className="font-mono text-xs">{r.destination_type}{r.destination_id ? ` · ${r.destination_id.slice(0, 8)}` : ''}</td>
                    <td className="text-center"><StatusPill status={r.enable_recording ? 'info' : 'neutral'} label={r.enable_recording ? 'on' : 'off'} /></td>
                    <td className="text-center"><StatusPill status={r.is_active ? 'success' : 'neutral'} label={r.is_active ? 'aktivní' : 'vypnuto'} /></td>
                </>
            )}
            modalContent={(editing, setEditing) => (
                <div className="grid grid-cols-3 gap-3">
                    <Field label="Název" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} required />
                    <Field label="DID pattern (např. +420123 nebo _+420X.)" value={editing.did_pattern} onChange={(v) => setEditing({ ...editing, did_pattern: v })} required mono />
                    <Field label="Priorita" value={editing.priority} onChange={(v) => setEditing({ ...editing, priority: parseInt(v || '0', 10) })} type="number" mono />
                    <Field label="Caller ID pattern (volitelné)" value={editing.caller_id_pattern || ''} onChange={(v) => setEditing({ ...editing, caller_id_pattern: v })} mono />
                    <div>
                        <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-secondary)' }}>Time Condition</label>
                        <select value={editing.time_condition_id || ''} onChange={(e) => setEditing({ ...editing, time_condition_id: e.target.value || null })}
                                className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm"
                                style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                            <option value="">— Vždy —</option>
                            {conds.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                    <div></div>
                    <div className="col-span-3 border-t pt-3" style={{ borderColor: 'var(--border-default)' }}>
                        <DestSelector
                            label="Primární destinace"
                            value={{ type: editing.destination_type, id: editing.destination_id }}
                            types={['extension', 'ring_group', 'queue', 'conference', 'call_flow', 'voicemail', 'external', 'hangup']}
                            onTypeChange={(t) => setEditing({ ...editing, destination_type: t, destination_id: null })}
                            onIdChange={(i) => setEditing({ ...editing, destination_id: i })}
                            extValue={editing.external_number}
                            onExtChange={(v) => setEditing({ ...editing, external_number: v })}
                        />
                    </div>
                    <div className="col-span-3 grid grid-cols-2 gap-3">
                        <DestSelector
                            label="Po pracovní době"
                            value={{ type: editing.after_hours_destination_type, id: editing.after_hours_destination_id }}
                            types={['extension', 'queue', 'voicemail', 'hangup']}
                            onTypeChange={(t) => setEditing({ ...editing, after_hours_destination_type: t || null })}
                            onIdChange={(i) => setEditing({ ...editing, after_hours_destination_id: i })}
                        />
                        <DestSelector
                            label="Fallback"
                            value={{ type: editing.fallback_destination_type, id: editing.fallback_destination_id }}
                            types={['extension', 'queue', 'voicemail', 'hangup']}
                            onTypeChange={(t) => setEditing({ ...editing, fallback_destination_type: t || null })}
                            onIdChange={(i) => setEditing({ ...editing, fallback_destination_id: i })}
                        />
                    </div>
                    <label className="col-span-3 inline-flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!editing.enable_recording} onChange={(e) => setEditing({ ...editing, enable_recording: e.target.checked })} />
                        Nahrávat hovor
                    </label>
                    <label className="col-span-3 inline-flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} />
                        Aktivní
                    </label>
                </div>
            )}
        />
    );
};

export const OutboundRoutes = () => {
    const [trunks, setTrunks] = useState([]);
    const [exts, setExts] = useState([]);
    useEffect(() => {
        Promise.all([api.get('/didww/trunks'), api.get('/extensions')]).then(([t, e]) => {
            setTrunks(t.data); setExts(e.data);
        });
    }, []);

    return (
        <RouteList
            kind="outbound"
            eyebrow="Routing"
            title="Outbound Routes"
            description="Pravidla pro odchozí hovory: pattern → trunk + caller ID rewrite. Kontroluje toll-fraud limity."
            blank={{
                name: '', dial_pattern: '_+420X.', strip_digits: 0, prepend_digits: '',
                trunk_id: null, caller_id_override: '',
                allowed_extension_ids: [], country_code: 'CZ',
                requires_pin: false, pin: '', priority: 100, is_active: true,
            }}
            columns={[
                { k: 'priority', label: 'Priorita' }, { k: 'name', label: 'Název' },
                { k: 'pattern', label: 'Dial pattern' }, { k: 'trunk', label: 'Trunk' },
                { k: 'pin', label: 'PIN', align: 'text-center' },
                { k: 'st', label: 'Stav', align: 'text-center' },
            ]}
            renderRow={(r) => (
                <>
                    <td className="py-2 font-mono">{r.priority}</td>
                    <td>{r.name}</td>
                    <td className="font-mono text-xs">{r.dial_pattern}</td>
                    <td className="font-mono text-xs">{(trunks.find((t) => t.id === r.trunk_id) || {}).name || '—'}</td>
                    <td className="text-center"><StatusPill status={r.requires_pin ? 'warning' : 'neutral'} label={r.requires_pin ? 'on' : 'off'} /></td>
                    <td className="text-center"><StatusPill status={r.is_active ? 'success' : 'neutral'} label={r.is_active ? 'aktivní' : 'vypnuto'} /></td>
                </>
            )}
            modalContent={(editing, setEditing) => (
                <div className="grid grid-cols-3 gap-3">
                    <Field label="Název" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} required />
                    <Field label="Dial pattern" value={editing.dial_pattern} onChange={(v) => setEditing({ ...editing, dial_pattern: v })} required mono />
                    <Field label="Priorita" value={editing.priority} onChange={(v) => setEditing({ ...editing, priority: parseInt(v || '0', 10) })} type="number" mono />
                    <Field label="Strip digits" value={editing.strip_digits} onChange={(v) => setEditing({ ...editing, strip_digits: parseInt(v || '0', 10) })} type="number" mono />
                    <Field label="Prepend digits" value={editing.prepend_digits || ''} onChange={(v) => setEditing({ ...editing, prepend_digits: v })} mono />
                    <Field label="Country (ISO)" value={editing.country_code || ''} onChange={(v) => setEditing({ ...editing, country_code: v.toUpperCase() })} mono />
                    <div>
                        <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-secondary)' }}>Trunk</label>
                        <select value={editing.trunk_id || ''} onChange={(e) => setEditing({ ...editing, trunk_id: e.target.value || null })}
                                className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm"
                                style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                            <option value="">—</option>
                            {trunks.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                    </div>
                    <Field label="Caller ID override" value={editing.caller_id_override || ''} onChange={(v) => setEditing({ ...editing, caller_id_override: v })} mono />
                    <Field label="PIN (volitelný)" value={editing.pin || ''} onChange={(v) => setEditing({ ...editing, pin: v })} type="password" mono />
                    <div className="col-span-3">
                        <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-secondary)' }}>Povolené klapky (prázdné = vše)</label>
                        <div className="mt-1 grid grid-cols-6 gap-1 p-2 border max-h-28 overflow-y-auto"
                             style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                            {exts.map((e) => (
                                <label key={e.id} className="inline-flex items-center gap-1 text-xs">
                                    <input type="checkbox"
                                           checked={(editing.allowed_extension_ids || []).includes(e.id)}
                                           onChange={(ev) => {
                                               const cur = editing.allowed_extension_ids || [];
                                               const next = ev.target.checked ? [...cur, e.id] : cur.filter((x) => x !== e.id);
                                               setEditing({ ...editing, allowed_extension_ids: next });
                                           }} />
                                    <span className="font-mono">{e.extension}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <label className="inline-flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!editing.requires_pin} onChange={(e) => setEditing({ ...editing, requires_pin: e.target.checked })} />
                        Vyžadovat PIN
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} />
                        Aktivní
                    </label>
                </div>
            )}
        />
    );
};

export default { InboundRoutes, OutboundRoutes };
